import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import { LEVEL, LIMIT_SYMBOL } from '../../../../LimitConfigurationBeta/components/constants';
import { Checkbox } from 'sharedComponents/Checkbox';
import { opLimitsConstants, SELECTOR_ACTIONS } from './constants';
import useNewCapability from 'sharedHooks/useNewCapability';
import { checkMaxLimitSelected, getSelectedLimitValues } from './helper';
import { useOutsideClickHandler } from '../../../../AssetDetails/Header/AssetLabels/helpers';
import { tabIDs } from '../../../../../DetailedInformation/constants';

const LimitSelectorComponent = (props) => {
    const { model, actions } = props;
    const newCapability = useNewCapability();
    const selectRef = useRef(null);
    const selectWrapRef = useRef(null);
    const justClosedRef = useRef(false);
    const dropdownHoverTimeoutRef = useRef(null);
    const { t: translate } = useTranslation();
    const [selectIsOpen, setSelectIsOpen] = useState(false);
    const opLimitCapabilities = useMemo(() => ({
        operationalParametersSelectLimit: newCapability.hasOperationalParametersSelectLimitCapability()
    }), [newCapability]);
    const selectedSignal = model?.trendKPIsList?.length && model?.trendKPIsList?.find((lmt) => lmt?.timeseriesKey === model?.selectedTrendKPIs[0]);
    const selectedLimitValues = getSelectedLimitValues(model?.maintainSelectedLimits, model?.selectedAsset, selectedSignal, model?.selectedOrganization, model?.selectedLimits);
    //  HANDLE OUTSIDE CLICK CLOSE
    const handleClickOutside = () => {
        if (selectRef.current) {
            setSelectIsOpen(false);
            selectRef.current.blur();
            justClosedRef.current = true;
            setTimeout(() => { justClosedRef.current = false; }, 200);
        }
    };
    useOutsideClickHandler(selectWrapRef, handleClickOutside);

    const handleMouseEnter = () => {
        if (dropdownHoverTimeoutRef.current) {
            clearTimeout(dropdownHoverTimeoutRef.current);
            dropdownHoverTimeoutRef.current = null;
        }
    };

    const handleMouseLeave = () => {
        dropdownHoverTimeoutRef.current = setTimeout(() => {
            setSelectIsOpen(false);
        }, 200);
    };

    useEffect(() => {
        return () => {
            if (dropdownHoverTimeoutRef.current) {
                clearTimeout(dropdownHoverTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (model?.selectedLimitsAction) {
            if (SELECTOR_ACTIONS.includes(model?.selectedLimitsAction?.action)) {
                setSelectIsOpen(true);
            }
        }

        return () => {
            actions.setSelectedLimitsAction(null);
        };
    }, [model?.selectedLimitsAction]);

    const createGroupforLimits = () => {
        const options = [];
        if (selectedSignal && selectedSignal?.thresholdList?.length) {
            const generalLimits = [], personalLimits = [];
            selectedSignal?.thresholdList?.map((lmt, i) => {
                const optionValue = `${lmt?.level}_${lmt?.direction}_${lmt?.limitValue}`;
                const symbol = lmt?.direction === LEVEL.UpperLimit || lmt?.direction === LEVEL.Upper ? LIMIT_SYMBOL.Greater : LIMIT_SYMBOL.Lower;
                const limitData = { ...lmt, label: `${lmt?.level}: ${symbol} ${lmt?.limitValue}`, value: optionValue, id: i, limitValue: +lmt?.limitValue };
                if (lmt?.isUserLimit) {
                    personalLimits.push(limitData);
                } else {
                    generalLimits.push(limitData);
                }
            });
            options.push({
                label: translate('ABB.Powertrain.Frontend.opGeneralLimits')?.toUpperCase(),
                options: checkMaxLimitSelected(generalLimits, selectedLimitValues)
            }, {
                label: translate('ABB.Powertrain.Frontend.opPersonalLimits')?.toUpperCase(),
                options: checkMaxLimitSelected(personalLimits, selectedLimitValues)
            });
        }
        return options || [];
    };

    useEffect(() => {
        if (model?.selectedTrendKPIs?.length > 1 && model?.selectedLimits?.length) {
            actions.setSelectedLimits(null);
            actions.setSelectedLimitsAction(null);
        }
        if (model?.selectedLimits?.length > 0 && model?.assetActiveTab !== tabIDs.operationalParametersv2 && model?.activeTabV2 !== tabIDs.operationalParameters) {
            actions.setSelectedLimits(null);
            actions.setSelectedLimitsAction(null);
        }

        // UPDATE LIMIT SELECTOR ON LIMIT UPDATES FROM LIMIT SETTINGS AND BY DEFAULT SELECT DEFAULT+CUSTOM LIMITS
        if (!model?.isDefaultLimitsSelected && model?.selectedTrendKPIs?.length === 1) {
            const signalLimits = createGroupforLimits();
            const customLimits = signalLimits?.find((sl) => !sl?.isUserLimits);
            if (customLimits?.options?.length) {
                actions.setSelectedLimits(customLimits?.options || []);
                actions.setIsDefaultLimitsSelected(true);
            }
        }
    }, [model?.selectedTrendKPIs, model?.assetActiveTab]);

    const formatGroupLabel = (data) => {
        return <div className='limit-group-label'><span>{data?.label} ({data?.options?.length})</span></div>;
    };
    const formatOptionLabel = (data) => {
        const selected = selectedLimitValues?.find((sl) => sl?.id === data?.id);
        return <div>
            <div className='d-flex'>
                <Checkbox checked={!isEmpty(selected)} />
                <div className='ms-1'>{data?.label}</div>
            </div>
        </div>;
    };
    const handleSelectLimits = (data, action) => {
        actions.setSelectedLimits(data);
        actions.setSelectedLimitsAction(action);
        actions.maintainLimitsAction(data, model?.selectedAsset, selectedSignal, model?.selectedOrganization);
        setSelectIsOpen(false);
        justClosedRef.current = true;
        setTimeout(() => { justClosedRef.current = false; }, 200);
        selectRef.current?.blur();
    };

    // CUSTOM VALUE CONTAINER FOR MORE THAN 2 LIMITS SELECTED
    const ValueContainer = props => {
        const { getValue, children } = props;
        const length = getValue()?.length;
        const newChildren = `${length} ${translate('ABB.Powertrain.Frontend.limitsTitle')?.toLowerCase()}`;
        return (
            <components.ValueContainer {...props}>
                {length > opLimitsConstants.MAX_SHOW_SELECTED_LIMITS ? newChildren : children}
            </components.ValueContainer>
        );
    };

    return model?.selectedTrendKPIs?.length === 1 &&
        selectedSignal &&
        selectedSignal?.thresholdList?.length &&
        opLimitCapabilities?.operationalParametersSelectLimit &&
        model?.selectedTrendsData?.data[0]?.values?.length !== 0 &&
        <div ref={selectWrapRef}
            tabIndex={0}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <Select
                isMulti
                ref={selectRef}
                options={createGroupforLimits()}
                className={`limits-multi-select ${selectIsOpen ? 'active' : ''}`}
                classNamePrefix='op-limits'
                placeholder={translate('ABB.Powertrain.Frontend.opLimitsSelectPlaceholder')}
                components={{
                    IndicatorSeparator: () => null,
                    ValueContainer
                }}
                formatGroupLabel={formatGroupLabel}
                formatOptionLabel={formatOptionLabel}
                onChange={handleSelectLimits}
                hideSelectedOptions={false}
                isSearchable={false}
                isOptionDisabled={(option) => option.isDisabled}
                maxMenuHeight={opLimitsConstants.MAX_OP_LIMITS_MENU_HEIGHT}
                value={selectedLimitValues}
                menuIsOpen={selectIsOpen}
                blurInputOnSelect={true}
                isFocused={selectIsOpen}
                noOptionsMessage={() => <>{translate('ABB.Powertrain.Frontend.noLimitsForSignal')}</>}
                onMenuOpen={() => {
                    if (justClosedRef.current) { return; }
                    setSelectIsOpen(true);
                }}
                onMenuClose={() => setSelectIsOpen(false)}
            />
        </div>;
};
LimitSelectorComponent.propTypes = {
    model: PropTypes.shape({
        trendKPIsList: PropTypes.array,
        selectedTrendKPIs: PropTypes.array,
        maintainSelectedLimits: PropTypes.object,
        selectedAsset: PropTypes.object,
        selectedOrganization: PropTypes.any,
        selectedLimits: PropTypes.array,
        selectedLimitsAction: PropTypes.object,
        assetActiveTab: PropTypes.string,
        isDefaultLimitsSelected: PropTypes.bool,
        selectedTrendsData: PropTypes.shape({
            data: PropTypes.array,
        }),
    }),
    actions: PropTypes.shape({
        setSelectedLimits: PropTypes.func,
        setSelectedLimitsAction: PropTypes.func,
        maintainLimitsAction: PropTypes.func,
        setIsDefaultLimitsSelected: PropTypes.func,
    }),
};
export default LimitSelectorComponent;
